import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, createContext, useContext } from 'react';
import { X, CheckCircle, XCircle, AlertCircle, Info } from 'lucide-react';
// Context
const ToastContext = createContext(undefined);
export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const addToast = (toast) => {
        const id = crypto.randomUUID();
        const newToast = {
            ...toast,
            id,
            duration: toast.duration || 5000
        };
        setToasts(prev => [...prev, newToast]);
        // Auto-remove após duração especificada
        if (newToast.duration && newToast.duration > 0) {
            setTimeout(() => {
                removeToast(id);
            }, newToast.duration);
        }
    };
    const removeToast = (id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    };
    const success = (title, message) => {
        addToast({ type: 'success', title, message });
    };
    const error = (title, message) => {
        addToast({ type: 'error', title, message, duration: 7000 });
    };
    const warning = (title, message) => {
        addToast({ type: 'warning', title, message });
    };
    const info = (title, message) => {
        addToast({ type: 'info', title, message });
    };
    const value = {
        addToast,
        removeToast,
        success,
        error,
        warning,
        info
    };
    return (_jsxs(ToastContext.Provider, { value: value, children: [children, _jsx(ToastContainer, { toasts: toasts, onRemove: removeToast })] }));
};
// Hook
export const useToast = () => {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error('useToast deve ser usado dentro de um ToastProvider');
    }
    return context;
};
const ToastContainer = ({ toasts, onRemove }) => {
    if (toasts.length === 0)
        return null;
    return (_jsx("div", { className: "fixed top-4 right-4 z-50 space-y-2 max-w-sm w-full", children: toasts.map(toast => (_jsx(ToastItem, { toast: toast, onRemove: onRemove }, toast.id))) }));
};
const ToastItem = ({ toast, onRemove }) => {
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        // Animar entrada
        setTimeout(() => setIsVisible(true), 10);
    }, []);
    const handleRemove = () => {
        setIsVisible(false);
        setTimeout(() => onRemove(toast.id), 150);
    };
    const getIcon = () => {
        switch (toast.type) {
            case 'success':
                return _jsx(CheckCircle, { className: "h-5 w-5 text-success-600" });
            case 'error':
                return _jsx(XCircle, { className: "h-5 w-5 text-danger-600" });
            case 'warning':
                return _jsx(AlertCircle, { className: "h-5 w-5 text-warning-600" });
            case 'info':
                return _jsx(Info, { className: "h-5 w-5 text-primary-600" });
        }
    };
    const getColorClasses = () => {
        switch (toast.type) {
            case 'success':
                return 'border-success-200 bg-success-50';
            case 'error':
                return 'border-danger-200 bg-danger-50';
            case 'warning':
                return 'border-warning-200 bg-warning-50';
            case 'info':
                return 'border-primary-200 bg-primary-50';
        }
    };
    return (_jsx("div", { className: `
        transform transition-all duration-300 ease-in-out
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
        ${getColorClasses()}
        border rounded-lg shadow-lg p-4
        animate-slide-in
      `, children: _jsxs("div", { className: "flex items-start space-x-3", children: [_jsx("div", { className: "flex-shrink-0 pt-0.5", children: getIcon() }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "text-sm font-medium text-gray-900", children: toast.title }), toast.message && (_jsx("p", { className: "mt-1 text-sm text-gray-600", children: toast.message })), toast.action && (_jsx("div", { className: "mt-2", children: _jsx("button", { onClick: toast.action.onClick, className: "text-sm font-medium text-primary-600 hover:text-primary-500", children: toast.action.label }) }))] }), _jsx("div", { className: "flex-shrink-0", children: _jsx("button", { onClick: handleRemove, className: "\n              rounded-md p-1 text-gray-400 hover:text-gray-600 \n              focus:outline-none focus:ring-2 focus:ring-primary-500\n              transition-colors\n            ", "aria-label": "Fechar notifica\u00E7\u00E3o", children: _jsx(X, { className: "h-4 w-4" }) }) })] }) }));
};
export default ToastContainer;
